import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, Plus, Check, X, ChevronDown, Filter, Loader2,
  AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const INCIDENT_TYPES = [
  'Fighting', 'Truancy', 'Disruptive Behavior', 'Bullying',
  'Vandalism', 'Insubordination', 'Cheating', 'Other',
];

const SEVERITY_META = {
  minor:    { label: 'Minor',    cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  moderate: { label: 'Moderate', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  serious:  { label: 'Serious',  cls: 'bg-red-100 text-red-700 border-red-200' },
};

function SeverityBadge({ severity }) {
  const m = SEVERITY_META[severity] || SEVERITY_META.minor;
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
  );
}

export default function BehaviorIncidentsPage() {
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [resolveId, setResolveId] = useState(null);
  const [resolveNote, setResolveNote] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterResolved, setFilterResolved] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [page, setPage] = useState(1);

  const [form, setForm] = useState({
    student_id: '', incident_type: '', description: '',
    severity: 'minor', action_taken: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/academics/classes'),
      api.get('/students?limit=500'),
    ]).then(([clsRes, stuRes]) => {
      setClasses(clsRes.data || []);
      setStudents(stuRes.data || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filterClass) params.set('class_id', filterClass);
      if (filterResolved !== '') params.set('resolved', filterResolved);
      if (filterSeverity) params.set('severity', filterSeverity);
      const res = await api.get(`/analytics/behavior-incidents?${params.toString()}`);
      if (res.success) {
        setIncidents(res.data.incidents || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error('Failed to load behavior incidents');
    } finally {
      setLoading(false);
    }
  }, [page, filterClass, filterResolved, filterSeverity]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.student_id || !form.incident_type || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/analytics/behavior-incidents', form);
      if (res.success) {
        toast.success('Incident recorded successfully');
        setShowForm(false);
        setForm({ student_id: '', incident_type: '', description: '', severity: 'minor', action_taken: '' });
        load();
      }
    } catch (err) {
      toast.error(err?.detail || 'Failed to record incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (id) => {
    try {
      const res = await api.patch(`/analytics/behavior-incidents/${id}/resolve`, { resolution_note: resolveNote });
      if (res.success) {
        toast.success('Incident marked as resolved');
        setResolveId(null);
        setResolveNote('');
        load();
      }
    } catch {
      toast.error('Failed to resolve incident');
    }
  };

  const openIncidents = incidents.filter(i => !i.resolved).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="behavior-incidents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-purple-600" />
            Behavior Incidents
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Track and manage student behavior incidents
          </p>
        </div>
        <button
          data-testid="add-incident-btn"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" />
          Record Incident
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-2xl font-bold text-slate-900">{total}</p>
          <p className="text-xs text-slate-500 mt-0.5">Total Incidents</p>
        </div>
        <div className="bg-white rounded-xl border border-orange-200 p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{openIncidents}</p>
          <p className="text-xs text-slate-500 mt-0.5">Open / Unresolved</p>
        </div>
        <div className="bg-white rounded-xl border border-emerald-200 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{total - openIncidents}</p>
          <p className="text-xs text-slate-500 mt-0.5">Resolved</p>
        </div>
      </div>

      {/* Record Incident Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-blue-200 p-5 mb-5 shadow-sm" data-testid="incident-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">Record New Incident</h3>
            <button onClick={() => setShowForm(false)}>
              <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Student */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Student *</label>
              <div className="relative">
                <select
                  value={form.student_id}
                  onChange={e => setForm(f => ({ ...f, student_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7"
                  data-testid="incident-student-select"
                >
                  <option value="">Select student...</option>
                  {students.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} — {s.class_name || 'No class'}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Incident type */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Type *</label>
              <div className="relative">
                <select
                  value={form.incident_type}
                  onChange={e => setForm(f => ({ ...f, incident_type: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7"
                  data-testid="incident-type-select"
                >
                  <option value="">Select type...</option>
                  {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Severity */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Severity</label>
              <div className="flex gap-2">
                {Object.entries(SEVERITY_META).map(([key, meta]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, severity: key }))}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                      form.severity === key ? `${meta.cls} shadow-sm` : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid={`severity-${key}`}
                  >
                    {meta.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Action taken */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Action Taken</label>
              <input
                type="text"
                value={form.action_taken}
                onChange={e => setForm(f => ({ ...f, action_taken: e.target.value }))}
                placeholder="e.g. Warning issued, parents contacted"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                data-testid="incident-action-input"
              />
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1 uppercase">Description *</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Describe what happened..."
                rows={3}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none resize-none"
                data-testid="incident-description-input"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition disabled:opacity-60"
              data-testid="incident-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Record Incident
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 flex flex-wrap gap-3 items-center">
        <Filter className="w-4 h-4 text-slate-400" />
        <div className="relative">
          <select
            value={filterClass}
            onChange={e => { setFilterClass(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7 min-w-[130px]"
            data-testid="incidents-filter-class"
          >
            <option value="">All Classes</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={filterSeverity}
            onChange={e => { setFilterSeverity(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7"
            data-testid="incidents-filter-severity"
          >
            <option value="">All Severities</option>
            {Object.entries(SEVERITY_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={filterResolved}
            onChange={e => { setFilterResolved(e.target.value); setPage(1); }}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none appearance-none pr-7"
            data-testid="incidents-filter-status"
          >
            <option value="">All Statuses</option>
            <option value="false">Open</option>
            <option value="true">Resolved</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        </div>
        <div className="ml-auto text-sm text-slate-400">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `${total} total`}
        </div>
      </div>

      {/* Incidents list */}
      {!loading && incidents.length === 0 && (
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-12 text-center">
          <ShieldAlert className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-semibold text-slate-600">No incidents recorded</p>
          <p className="text-sm text-slate-400 mt-1">Use "Record Incident" to start tracking behavior data.</p>
        </div>
      )}

      <div className="space-y-3">
        {incidents.map(inc => (
          <div
            key={inc.id}
            data-testid="incident-card"
            className={`bg-white rounded-xl border p-4 shadow-sm ${inc.resolved ? 'border-slate-200 opacity-75' : 'border-slate-200'}`}
          >
            <div className="flex items-start gap-3">
              {/* Status icon */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                inc.resolved ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                {inc.resolved
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  : <AlertTriangle className="w-4 h-4 text-red-500" />
                }
              </div>

              <div className="flex-1 min-w-0">
                {/* Top row */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800 text-sm">{inc.student_name}</span>
                  {inc.class_name && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{inc.class_name}</span>
                  )}
                  <SeverityBadge severity={inc.severity} />
                  {inc.resolved && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200 font-semibold">Resolved</span>
                  )}
                </div>

                {/* Type + date */}
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-medium text-purple-600">{inc.incident_type}</span>
                  <span className="text-xs text-slate-400">{(inc.created_at || '').slice(0, 10)}</span>
                  {inc.reported_by_name && (
                    <span className="text-xs text-slate-400">· Reported by {inc.reported_by_name}</span>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-slate-600 mt-1.5">{inc.description}</p>

                {inc.action_taken && (
                  <p className="text-xs text-slate-500 mt-1">
                    <span className="font-medium">Action taken:</span> {inc.action_taken}
                  </p>
                )}

                {inc.resolved && inc.resolution_note && (
                  <p className="text-xs text-emerald-700 mt-1">
                    <span className="font-medium">Resolution:</span> {inc.resolution_note}
                  </p>
                )}

                {/* Resolve inline form */}
                {resolveId === inc.id && (
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={resolveNote}
                      onChange={e => setResolveNote(e.target.value)}
                      placeholder="Resolution note (optional)"
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm outline-none"
                      data-testid="resolve-note-input"
                    />
                    <button
                      onClick={() => handleResolve(inc.id)}
                      className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700"
                      data-testid="confirm-resolve-btn"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => { setResolveId(null); setResolveNote(''); }}
                      className="px-3 py-1.5 border border-slate-200 text-xs rounded-lg hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {/* Resolve button */}
              {!inc.resolved && resolveId !== inc.id && (
                <button
                  onClick={() => setResolveId(inc.id)}
                  className="flex items-center gap-1 text-xs text-emerald-600 border border-emerald-200 px-2.5 py-1.5 rounded-lg hover:bg-emerald-50 transition flex-shrink-0"
                  data-testid="resolve-incident-btn"
                >
                  <Check className="w-3.5 h-3.5" />
                  Resolve
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {total > 30 && (
        <div className="flex justify-center gap-2 mt-5">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            disabled={incidents.length < 30}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
